import { TextInput } from "@ameelio/ui";
import { Alert, AlertTitle, Box, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import StepperFormWrapper from "../lib/StepperFormWrapper";
import { StepperType } from "../lib/StepperWrapper";
import { ScreenTitle } from "../lib/typography";

import Link from "../lib/Link";
import {
  HiddenRelationship,
  PersonalRelationship,
  ProfessionalRelationship,
} from "../lib/Relationship";
import RelationshipInput from "../lib/RelationshipInput";
import { hasMaxLength, isRequiredString, mergeRules } from "../lib/validate";
import { RelationshipFormCriteria } from "./types";

type Props = {
  stepper: StepperType;
  relationship?: string;
  onSubmit: (data: RelationshipFormCriteria) => void | Promise<void>;
};

export default function RelationshipForm({
  stepper,
  relationship,
  onSubmit,
}: Props) {
  const { t } = useTranslation();

  const isStandardRelationship =
    relationship &&
    (Object.values(PersonalRelationship).includes(
      relationship as PersonalRelationship
    ) ||
      Object.values(ProfessionalRelationship).includes(
        relationship as ProfessionalRelationship
      ) ||
      Object.values(HiddenRelationship).includes(
        relationship as HiddenRelationship
      ));
  const isOtherRelationship = relationship && !isStandardRelationship;

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    getValues,
    formState: { isSubmitting, isValid },
  } = useForm<RelationshipFormCriteria>({
    mode: "onChange",
    defaultValues: {
      relationship:
        relationship && isStandardRelationship
          ? relationship
          : relationship
            ? PersonalRelationship.Other
            : "",
      relationshipDetails: isOtherRelationship ? relationship : "",
    },
  });

  const selectedRelationship = watch("relationship") || "";
  const relationshipDetailsValue = watch("relationshipDetails") || "";

  // Organization types currently only support a limited number of professional
  // relationships.
  const suggestOrganization = [ProfessionalRelationship.LegalAdvisor].includes(
    selectedRelationship as ProfessionalRelationship
  );

  return (
    <StepperFormWrapper
      stepper={stepper({
        loading: isSubmitting,
        disabled:
          selectedRelationship === PersonalRelationship.Other
            ? !(relationshipDetailsValue || "").trim() || !isValid
            : !selectedRelationship,
      })}
      handleSubmit={handleFormSubmit(() =>
        onSubmit({
          relationship: getValues("relationship"),
          relationshipDetails: getValues("relationshipDetails"),
        })
      )}
    >
      <ScreenTitle>{t("How are you related?")}</ScreenTitle>
      <Stack
        spacing={3}
        sx={{
          marginTop: 3,
          marginBottom: 6,
        }}
      >
        <RelationshipInput control={control} name="relationship" />
        {selectedRelationship === PersonalRelationship.Other && (
          <TextInput
            control={control}
            name="relationshipDetails"
            label={t("Relationship type")}
            rules={mergeRules(
              isRequiredString(t("Please enter your relationship type.")),
              hasMaxLength(
                30,
                t(
                  "Relationship type can't be longer than {{number}} characters.",
                  { number: 30 }
                )
              )
            )}
          />
        )}
        {suggestOrganization && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              mt: 1,
              alignItems: "flex-start",
              backgroundColor: "white",
            }}
          >
            <Stack
              sx={{
                gap: 3,
              }}
            >
              <AlertTitle>
                {t("Do you work with incarcerated people?")}
              </AlertTitle>

              <Stack
                sx={{
                  gap: 2,
                }}
              >
                <Typography variant="body1">
                  {t(
                    "Add organization details to your Ameelio Connect account to unlock additional features."
                  )}
                </Typography>

                <Typography variant="body1">
                  {t("With an Ameelio Connect organization:")}
                </Typography>

                <Box component="ul" sx={{ m: 0, ml: 4, p: 0 }}>
                  <li>
                    <Typography variant="body1">
                      {t(
                        "Facility staff will see your professional details while reviewing your contact and scheduling requests"
                      )}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      {t(
                        "Participating facilities may offer privileged communication and additional schedules"
                      )}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      {t("You can work in a team with your colleagues")}
                    </Typography>
                  </li>
                </Box>
              </Stack>
              <Box>
                <Link button variant="contained" to="/organization/new">
                  {t("Create organization")}
                </Link>
              </Box>
            </Stack>
          </Alert>
        )}
      </Stack>
    </StepperFormWrapper>
  );
}
